import { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { SerializedError } from '@reduxjs/toolkit';
import QRCode from 'qrcode';
import { useInitializeMfaSetupQuery, useVerifyMfaSetupMutation } from '@/app/services/authApi';
import { useNotification } from '@/hooks/useNotification';
import { osirisTokens } from '@/theme/tokens';
import { AuthHeader, AuthCodeInput, AuthPrimaryButton } from './AuthPrimitives';

function formatSecretGroups(secret: string): string {
  // Render the manual key in 4-char groups for readability: "ABCD EFGH IJKL ..."
  return secret.replace(/\s+/g, '').match(/.{1,4}/g)?.join(' ') ?? secret;
}

export function SetupMfaPage() {
  const [code, setCode] = useState<string[]>(['', '', '', '', '', '']);
  const [copied, setCopied] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);

  // RTK Query cache is the source of truth — no local copy of the setup data.
  const { data: mfaSetup } = useInitializeMfaSetupQuery();
  const [verifyMfaSetup, { isLoading: isVerifying }] = useVerifyMfaSetupMutation();
  const { showErrorNotification } = useNotification();

  const keyUri = mfaSetup?.keyUri;
  useEffect(() => {
    if (!keyUri) return;
    let cancelled = false;
    QRCode.toDataURL(keyUri, {
      errorCorrectionLevel: 'M',
      type: 'image/png',
      margin: 2,
      color: { dark: '#000000', light: '#FFFFFF' },
      width: 320,
    })
      .then((qrDataUrl) => {
        if (!cancelled) setQrCodeDataUrl(qrDataUrl);
      })
      .catch((err) => {
        console.error('Failed to generate QR code:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [keyUri]);

  const handleCopySecret = async () => {
    if (!mfaSetup?.secret) return;
    try {
      await navigator.clipboard.writeText(mfaSetup.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const codeString = code.join('');
    if (codeString.length !== 6) {
      showErrorNotification('Enter the 6-digit code from your authenticator');
      return;
    }
    try {
      await verifyMfaSetup({ code: codeString }).unwrap();
    } catch (verifyError) {
      showErrorNotification(verifyError as FetchBaseQueryError | SerializedError);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} className="auth-setup-mfa-form">
      <AuthHeader title="Set Up Authenticator" subtitle="Scan with any TOTP authenticator app" />

      <Box
        className="auth-mfa-qr"
        sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}
      >
        {qrCodeDataUrl ? (
          <Box
            component="img"
            src={qrCodeDataUrl}
            alt="Authenticator QR code"
            sx={{ width: 160, height: 160, borderRadius: '4px' }}
          />
        ) : (
          <Box
            sx={{
              width: 160,
              height: 160,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px dashed ${osirisTokens.border.lighter}`,
              borderRadius: '4px',
            }}
          >
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>

      <Typography
        sx={{
          fontFamily: osirisTokens.fonts.display,
          fontSize: 14,
          color: osirisTokens.text.muted,
          letterSpacing: '0.04em',
          mb: 1,
        }}
      >
        Can't scan? Use manual key
      </Typography>

      <Box
        className="auth-mfa-manual-key"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          height: 40,
          px: 2,
          mb: 2.5,
          border: `1px solid ${osirisTokens.border.lighter}`,
          borderRadius: '4px',
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            fontFamily: osirisTokens.fonts.mono,
            fontSize: 14,
            color: osirisTokens.text.primary,
            letterSpacing: '0.02em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {mfaSetup?.secret ? formatSecretGroups(mfaSetup.secret) : '—'}
        </Box>
        <Box
          component="button"
          type="button"
          onClick={handleCopySecret}
          disabled={!mfaSetup?.secret}
          className="auth-mfa-copy"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: mfaSetup?.secret ? 'pointer' : 'not-allowed',
            color: osirisTokens.text.active,
            fontFamily: osirisTokens.fonts.display,
            fontSize: 14,
            letterSpacing: '0.06em',
            textTransform: 'lowercase',
            '&:hover': { textDecoration: 'underline' },
            '&:disabled': { opacity: 0.5, textDecoration: 'none' },
          }}
        >
          {copied ? 'copied' : 'copy'}
          <ContentCopyIcon sx={{ fontSize: 18 }} />
        </Box>
      </Box>

      <Typography
        sx={{
          fontFamily: osirisTokens.fonts.display,
          fontSize: 14,
          color: osirisTokens.text.muted,
          letterSpacing: '0.04em',
          mb: 1,
        }}
      >
        MFA code
      </Typography>
      <Box sx={{ mb: 3 }}>
        <AuthCodeInput value={code} onChange={setCode} autoFocus={false} />
      </Box>

      <AuthPrimaryButton isLoading={isVerifying} className="auth-mfa-verify-submit">
        Verify & Continue
      </AuthPrimaryButton>
    </Box>
  );
}
